import Vue from "vue";
import Router, { NavigationGuardNext, Route } from "vue-router";
import { IStore } from "@/store";
import { AppAbilityType, buildAbilityFor, defineRulesFor } from "@/services/auth/ability";
import CallbackPage from "@/pages/CallbackPage.vue";
import DefaultLayout from "@/layout/default/DefaultLayout.vue";
import ErrorPage from "@/pages/ErrorPage.vue";
import NotFoundErrorPage from "@/pages/NotFoundErrorPage.vue";
import FirmsPage from "@/pages/FirmsPage.vue";
import AdministrationPage from "@/pages/AdministrationPage.vue";
import UsersPage from "@/pages/user/UsersPage.vue";
import UserEditPage from "@/pages/user/UserEditPage.vue";
import ClientsPage from "@/pages/client/ClientsPage.vue";
import ClientAddPage from "@/pages/client/ClientAddPage.vue";
import ClientEditPage from "@/pages/client/ClientEditPage.vue";
import SubclientsPage from "@/pages/subclient/SubclientsPage.vue";
import SubclientAddPage from "@/pages/subclient/SubclientAddPage.vue";
import SubclientEditPage from "@/pages/subclient/SubclientEditPage.vue";
import ServicepointsPage from "@/pages/servicepoint/ServicepointsPage.vue";
import ServicepointAddPage from "@/pages/servicepoint/ServicepointAddPage.vue";
import ServicepointEditPage from "@/pages/servicepoint/ServicepointEditPage.vue";
import OrdersPage from "@/pages/orders/OrdersPage.vue";
import DepositPage from "@/pages/deposit/DepositPage.vue";
import ReportsPage from "@/pages/reports/ReportsPage.vue";
import CountriesPage from "@/pages/countries/CountriesPage.vue";

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  routes: [
    {
      path: "/",
      component: DefaultLayout,
      redirect: "/firms",
      children: [
        {
          path: "firms",
          name: "firms",
          component: FirmsPage,
          meta: {
            resource: "FirmsPage",
          },
        },
        {
          path: "orders",
          name: "orders",
          component: OrdersPage,
          meta: {
            resource: "OrdersPage",
          },
        },
        {
          path: "deposit",
          name: "deposit",
          component: DepositPage,
          meta: {
            resource: "DepositPage",
          },
        },
        {
          path: "administration",
          name: "administration",
          component: AdministrationPage,
          redirect: "administration/users",
          meta: {
            resource: "AdministrationPage",
          },
          children: [
            {
              path: "users",
              name: "users",
              component: UsersPage,
              meta: {
                resource: "UsersPage",
                alternativeRedirect: "administration/clients",
              },
            },
            {
              path: "users/:id",
              name: "edit-user",
              component: UserEditPage,
              meta: {
                resource: "UserEditPage",
              },
            },
            {
              path: "clients",
              name: "clients",
              component: ClientsPage,
              meta: {
                resource: "ClientsPage",
              },
            },
            {
              path: "clients/add",
              name: "add-client",
              component: ClientAddPage,
              meta: {
                resource: "ClientAddPage",
              },
            },
            {
              path: "clients/:id",
              name: "edit-client",
              component: ClientEditPage,
              meta: {
                resource: "ClientEditPage",
              },
            },
            {
              path: "subclients",
              name: "subclients",
              component: SubclientsPage,
              meta: {
                resource: "SubclientsPage",
              },
            },
            {
              path: "subclients/add",
              name: "add-subclient",
              component: SubclientAddPage,
              meta: {
                resource: "SubclientAddPage",
              },
            },
            {
              path: "subclients/:id",
              name: "edit-subclient",
              component: SubclientEditPage,
              meta: {
                resource: "SubclientEditPage",
              },
            },
            {
              path: "servicepoints",
              name: "servicepoints",
              component: ServicepointsPage,
              meta: {
                resource: "ServicepointsPage",
              },
            },
            {
              path: "servicepoints/add",
              name: "add-servicepoint",
              component: ServicepointAddPage,
              meta: {
                resource: "ServicepointAddPage",
              },
            },
            {
              path: "servicepoints/:id",
              name: "edit-servicepoint",
              component: ServicepointEditPage,
              meta: {
                resource: "ServicepointEditPage",
              },
            },
            {
              path: "countries",
              name: "countries",
              component: CountriesPage,
              meta: {
                resource: "CountriesPage",
              },
            },
          ],
        },
        {
          path: "reports",
          name: "reports",
          component: ReportsPage,
          meta: {
            resource: "ReportsPage",
          },
        },
        {
          path: "callback",
          name: "callback",
          component: CallbackPage,
          meta: {
            anonymous: true,
          },
        },
        {
          path: "404",
          name: "404",
          component: NotFoundErrorPage,
        },
        {
          path: "error",
          name: "error",
          component: ErrorPage,
        },
      ],
    },
    { path: "*", redirect: "/404" },
  ],
});

export const useRouter = (store: IStore): Router => {
  const abilities = buildAbilityFor(store.getters.getLoggedUser, store.getters.getCountryConfig);

  router.beforeEach(async (to, _from, next) => {
    const canNavigate = () => {
      return (
        to.meta?.anonymous ||
        abilities.can(to.meta?.action || "read", to.meta?.resource) ||
        !to.meta?.resource
      );
    };

    if (to.matched.some((record) => record.meta.anonymous)) {
      next();
    } else {
      await store.dispatch("setCurrentLocation", `${window.origin}${to.fullPath}`);

      if (!store.getters.getSignedIn) {
        await store.dispatch("initialize");
        abilities.update(
          defineRulesFor(store.getters.getLoggedUser, store.getters.getCountryConfig)
        );
      }

      if (store.getters.getSignedIn) {
        if (!canNavigate()) {
          redirect(to, next, abilities);
        }

        next();
      }
    }
  });

  return router;
};

function redirect(to: Route, next: NavigationGuardNext, abilities: AppAbilityType) {
  if (
    (to.redirectedFrom === "/" || window.location.pathname === "/callback") &&
    abilities.rules?.filter((rule) => rule.subject.toString().includes("Page")).length
  ) {
    const resource = abilities.rules
      ?.filter((rule) => rule.subject.toString().includes("Page"))[0]
      .subject.toString();
    return next(router.getRoutes().find((route) => route.meta.resource === resource));
  } else if (to.redirectedFrom && to.meta?.alternativeRedirect) {
    return next(to.meta.alternativeRedirect);
  } else {
    return next({
      name: "error",
      query: {
        type: "403",
      },
    });
  }
}
